import React, { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { makeStyles } from "@material-ui/core";
import { submitFeedback } from "actions/chat-actions";
import FeedbackDialog from "./FeedbackDialog";

const useStyles = makeStyles(() => ({
  wrap: {
    marginTop: "16px",
    paddingTop: "10px",
    borderTop: "1px solid #F0F2F5",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  row: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    rowGap: "4px",
  },
  label: {
    fontSize: "12px",
    color: "#9CA3AF",
    fontFamily: "'IBM Plex Sans', sans-serif",
    marginRight: "10px",
  },
  starsRow: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
  },
  starBtn: {
    background: "none",
    border: "none",
    padding: "2px",
    cursor: "pointer",
    fontSize: "18px",
    lineHeight: 1,
    color: "#D1D5DB",
    transition: "color 120ms ease",
    outline: "none",
  },
  starFilled: {
    color: "#F5A623",
  },
  thanks: {
    fontSize: "12px",
    color: "#10B981",
    fontFamily: "'IBM Plex Sans', sans-serif",
    marginLeft: "12px",
    marginRight: "12px",
    opacity: 0,
    transition: "opacity 200ms ease",
  },
  thanksVisible: {
    opacity: 1,
  },
  link: {
    background: "none",
    border: "none",
    padding: 0,
    cursor: "pointer",
    fontSize: "12px",
    color: "#2563eb",
    fontFamily: "'IBM Plex Sans', sans-serif",
    textDecoration: "none",
    "&:hover": {
      textDecoration: "underline",
    },
  },
  errorText: {
    fontSize: "11px",
    color: "#DC2626",
    fontFamily: "'IBM Plex Sans', sans-serif",
  },
}));

function hasDetailedFields(feedback) {
  if (!feedback) return false;
  return (
    feedback.accuracy != null ||
    feedback.completeness != null ||
    feedback.clarity != null ||
    feedback.visualization != null ||
    (Array.isArray(feedback.issue_tags) && feedback.issue_tags.length > 0) ||
    (typeof feedback.comment === "string" && feedback.comment.length > 0) ||
    (typeof feedback.expected_answer === "string" && feedback.expected_answer.length > 0)
  );
}

const FeedbackBar = ({ messageId, feedback }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [hoverRating, setHoverRating] = useState(0);
  const [optimisticRating, setOptimisticRating] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [errorVisible, setErrorVisible] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const errorTimerRef = useRef(null);
  const prevFeedbackRef = useRef(feedback);

  // Reconcile optimistic state with redux state
  useEffect(() => {
    if (feedback && feedback.rating != null) {
      // Server confirmed — clear optimistic state
      setOptimisticRating(null);
      setSubmitting(false);
      setErrorVisible(false);
      if (errorTimerRef.current) {
        clearTimeout(errorTimerRef.current);
        errorTimerRef.current = null;
      }
    } else if (
      prevFeedbackRef.current &&
      prevFeedbackRef.current.rating != null &&
      (!feedback || feedback.rating == null) &&
      submitting
    ) {
      // Was set, now isn't, and we were submitting — failure
      // (Handled directly via the dispatch promise below; this is defensive.)
    }
    prevFeedbackRef.current = feedback;
  }, [feedback, submitting]);

  useEffect(() => {
    return () => {
      if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
    };
  }, []);

  const savedRating = feedback?.rating ?? null;
  const effectiveRating = optimisticRating || savedRating;
  const displayRating = hoverRating || optimisticRating || savedRating || 0;
  const showThanks = !!effectiveRating && !errorVisible;
  const showLink = !!effectiveRating && !errorVisible;
  const linkText = hasDetailedFields(feedback) ? "Edit feedback" : "Give detailed feedback →";

  const handleStarClick = (n) => {
    setOptimisticRating(n);
    setSubmitting(true);
    setErrorVisible(false);
    if (errorTimerRef.current) {
      clearTimeout(errorTimerRef.current);
      errorTimerRef.current = null;
    }
    // Build payload preserving any existing detailed fields when re-rating
    const payload = { rating: n };
    if (feedback) {
      if (feedback.accuracy != null) payload.accuracy = feedback.accuracy;
      if (feedback.completeness != null) payload.completeness = feedback.completeness;
      if (feedback.clarity != null) payload.clarity = feedback.clarity;
      if (feedback.visualization != null) payload.visualization = feedback.visualization;
      if (Array.isArray(feedback.issue_tags) && feedback.issue_tags.length > 0) {
        payload.issue_tags = feedback.issue_tags;
      }
      if (feedback.comment) payload.comment = feedback.comment;
      if (feedback.expected_answer) payload.expected_answer = feedback.expected_answer;
    }
    dispatch(submitFeedback(messageId, payload));
  };

  // Detect failure: if we're submitting and feedback didn't update within ~5s, show error.
  // Simpler approach: use a dedicated success/failure observation via a side effect.
  useEffect(() => {
    if (!submitting) return;
    const timeout = setTimeout(() => {
      // If still submitting after 6 seconds and feedback hasn't matched optimistic, treat as failure
      if (
        optimisticRating != null &&
        (!feedback || feedback.rating !== optimisticRating)
      ) {
        setOptimisticRating(null);
        setSubmitting(false);
        setErrorVisible(true);
        errorTimerRef.current = setTimeout(() => {
          setErrorVisible(false);
        }, 4000);
      }
    }, 6000);
    return () => clearTimeout(timeout);
  }, [submitting, optimisticRating, feedback]);

  const stars = [1, 2, 3, 4, 5].map((n) => {
    const filled = n <= displayRating;
    return (
      <button
        key={n}
        type="button"
        className={`${classes.starBtn} ${filled ? classes.starFilled : ""}`}
        aria-label={`Rate ${n} of 5`}
        onClick={() => handleStarClick(n)}
        onMouseEnter={() => setHoverRating(n)}
        onMouseLeave={() => setHoverRating(0)}
      >
        {filled ? "★" : "☆"}
      </button>
    );
  });

  return (
    <div className={classes.wrap}>
      <div className={classes.row}>
        <span className={classes.label}>Was this helpful?</span>
        <span className={classes.starsRow}>{stars}</span>
        <span
          className={`${classes.thanks} ${showThanks ? classes.thanksVisible : ""}`}
          aria-hidden={!showThanks}
        >
          Thanks for your feedback!
        </span>
        {showLink && (
          <button
            type="button"
            className={classes.link}
            onClick={() => setDialogOpen(true)}
          >
            {linkText}
          </button>
        )}
      </div>
      {errorVisible && (
        <div className={classes.errorText}>Couldn't save — tap a star to retry</div>
      )}
      <FeedbackDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        messageId={messageId}
        feedback={feedback}
      />
    </div>
  );
};

export default FeedbackBar;
